/* import __COLOCATED_TEMPLATE__ from './assign-permissions.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */
import Component from '@glimmer/component';
import { type PulseAccordion } from 'glint/pulse';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';
import type IntlService from 'embercom/services/intl';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import { isEmpty } from '@ember/utils';
import { taskFor } from 'ember-concurrency-ts';
import type AppService from 'embercom/services/app-service';

interface Signature {
  Args: {
    accordion: PulseAccordion;
    onContinue: () => void;
  };
}

export default class AssignPermissionsComponent extends Component<Signature> {
  @service declare appService: AppService;
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;

  get app() {
    return this.appService.app;
  }

  get salesforcePlatform() {
    return this.finStandaloneService.salesforcePlatform;
  }

  get salesforceConfig() {
    return this.salesforcePlatform.salesforceConfig!;
  }

  get salesforceSetupData() {
    return this.salesforcePlatform.salesforceSetupData;
  }

  get isDisabled() {
    return this.salesforceConfig?.isPendingSetup || !this.salesforceSetupData.hasPermissionSet;
  }

  get isComplete() {
    return (
      this.salesforceSetupData.hasPermissionSetAssignedToInstaller &&
      !this.hasFailedPermissionsHealthcheck
    );
  }

  get hasFailedPermissionsHealthcheck() {
    if (!this.app.canUseHealthcheck) {
      return false;
    }

    return this.salesforceSetupData.permissionsHealthcheck.any(
      (healthcheck) => healthcheck.status !== 'ok',
    );
  }

  get failingHealthchecks() {
    return this.salesforceSetupData.permissionsHealthcheck.filter(
      (healthcheck) => healthcheck.status !== 'ok',
    );
  }

  get isLoading() {
    return (
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforceSetupData ||
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforcePermissions ||
      this.salesforcePlatform.salesforceDataLoader.isLoadingSalesforcePermissionsHealthcheck
    );
  }

  get salesforceUserInstallerName() {
    if (this.isLoading || isEmpty(this.salesforceConfig.userId)) {
      return undefined;
    }

    let user = this.salesforcePlatform.salesforceSetupData.admins.find(
      (admin) => admin.id === this.salesforceConfig.userId,
    );

    if (user && user.name) {
      return user.name;
    }

    return this.salesforceConfig.userId;
  }

  get installationSteps() {
    return [
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.connect.assign-permissions-section.step-1',
          {
            finUserName: this.salesforceConfig.finSalesforceUserId,
            htmlSafe: true,
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.connect.assign-permissions-section.step-2',
          {
            htmlSafe: true,
          },
        ),
      },
      {
        label: this.intl.t(
          'standalone.deploy.salesforce.cases.connect.assign-permissions-section.step-3',
          {
            htmlSafe: true,
          },
        ),
      },
    ];
  }

  @action
  openUrl() {
    safeWindowOpen(
      `${this.salesforceConfig.lightningForceUrl}/lightning/setup/PermSets/page?address=/udd/PermissionSet/assignPermissionSet.apexp?userId=${this.salesforceConfig.userId}`,
      '_blank',
    );
  }

  @action
  reloadPermissionsHealthcheck() {
    taskFor(
      this.salesforcePlatform.salesforceDataLoader.loadSalesforcePermissionsHealthcheck,
    ).perform();
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Deploy::Salesforce::Cases::Connect::AssignPermissions': typeof AssignPermissionsComponent;
  }
}
